import React, { useEffect } from "react";
import Box from "@mui/material/Box/Box";
import { Button, TextField } from "@mui/material";
import GeniePage from "../../../components/special/genie-page/GeniePage";
import GenieText from "../../../components/core/genie-text/GenieText";
import { apiUrl, getApiHeaders } from "../../../utils/apiUrl";
import { Image } from "../../../types/api/image";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle } from "@mui/icons-material";
import { useAuth0 } from "@auth0/auth0-react";
import useUser from "../../../queries/routes/useUser";
import useSplash from "../../../queries/routes/useSplash";

function Onboarding() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { org_id } = useParams();
  const { data: user } = useUser();

  const { data: image_set, refetch } = useSplash(org_id!);

  const [slogan, setSlogan] = React.useState<string>("");

  useEffect(() => {
    if (user?.organizations) {
      setSlogan(
        user.organizations.find((org) => org.id === org_id)?.slogan || ""
      );
    }
  }, [user, org_id]);

  const updateOnboarding = async (
    image: Image,
    type: "logo" | "splash_image"
  ) => {
    const data = {
      id: image_set?.id || null,
      organization_id: user?.purchasing_organizations[0].organization.id,
      [type]: image,
    };

    const update = async () => {
      await fetch(apiUrl(`/api/organization/image_set`), {
        ...getApiHeaders(await getAccessTokenSilently()),
        method: "PUT",
        body: JSON.stringify(data),
      });
      await refetch();
    };
    update();
  };

  const updateOrganization = async (slogan: string) => {
    if (user?.organizations) {
      const org = user.organizations.find((org) => org_id === org.id);

      const data = {
        ...org,
        slogan: slogan,
      };

      const update = async () => {
        const response = await fetch(apiUrl(`/api/organization/${org_id}`), {
          ...getApiHeaders(await getAccessTokenSilently()),
          method: "PUT",
          body: JSON.stringify(data),
        });
        await response.json();
        navigate(`/app/${org_id}/welcome`);
      };
      update();
    }
  };

  async function handleSubmit(
    event: React.FormEvent<HTMLInputElement>,
    type: "logo" | "splash_image"
  ) {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      const myFile = target.files[0];
      const formData = new FormData();
      formData.append("file", myFile, myFile.name);
      const response = await fetch(apiUrl(`/api/media/${org_id}/upload`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
        body: formData,
        redirect: "follow",
      });
      const data = (await response.json()) as Image;
      updateOnboarding(data, type);
    }
  }

  return (
    <GeniePage>
      <Box mb={4}>
        <GenieText fontSize={"medium"} fontWeight={"bold"}>
          Let's get started with Genie by setting up your business.
        </GenieText>
      </Box>

      <Box mb={2}>
        <Box display={"flex"} alignItems={"center"}>
          {image_set?.logo && (
            <CheckCircle
              width={24}
              height={24}
              sx={{ mr: 2, display: "inline", color: "primary" }}
              color={"info"}
            />
          )}
          <GenieText>1. Add your brand logo</GenieText>
        </Box>
        <Box>
          {image_set?.logo && (
            <img
              alt={"logo"}
              height={100}
              width={100}
              src={image_set.logo.cloudinary_url}
            />
          )}
        </Box>

        <Button sx={{ mt: 1 }} variant="outlined" component="label">
          {image_set?.logo ? "Reupload Logo" : "Upload Logo"}
          <input
            accept={"image/*"}
            type="file"
            onChange={(event) => handleSubmit(event, "logo")}
            id={"uploadLogo"}
            hidden
          />
        </Button>
        <GenieText subdued fontSize={"small"}>
          This must be a square image
        </GenieText>
      </Box>

      <Box mb={2}>
        <Box display={"flex"} alignItems={"center"}>
          {image_set?.splash_image && (
            <CheckCircle
              width={24}
              height={24}
              sx={{ mr: 2, display: "inline" }}
              color={"info"}
            />
          )}
          <GenieText>2. Add your cover photo</GenieText>
        </Box>
        <Box>
          {image_set?.splash_image && (
            <img
              alt={"splash"}
              height={"auto"}
              width={"100%"}
              src={image_set.splash_image.cloudinary_url}
            />
          )}
        </Box>
        <Button sx={{ mt: 1 }} variant="outlined" component="label">
          {image_set?.splash_image ? "Reupload Splash" : "Upload Splash"}
          <input
            accept={"image/*"}
            type="file"
            onChange={(event) => handleSubmit(event, "splash_image")}
            id={"uploadSplash"}
            hidden
          />
        </Button>
        <GenieText subdued fontSize={"small"}>
          This must be a wide landscape image
        </GenieText>
      </Box>

      <Box mb={2}>
        <Box display={"flex"} alignItems={"center"}>
          {user?.organizations[0].slogan && (
            <CheckCircle
              width={24}
              height={24}
              sx={{ mr: 2, display: "inline" }}
              color={"info"}
            />
          )}
          <GenieText>3. Add your slogan</GenieText>
        </Box>
        <TextField
          sx={{ mt: 1 }}
          variant="outlined"
          fullWidth
          label="Slogan"
          placeholder="Your slogan"
          value={slogan}
          onChange={(event) => setSlogan(event.target.value)}
        />
        <GenieText subdued fontSize={"small"}>
          This is a short phrase that describes your brand
        </GenieText>
      </Box>
      <Button
        variant="outlined"
        onClick={() => updateOrganization(slogan || "")}
      >
        Save
      </Button>
    </GeniePage>
  );
}

export default Onboarding;
